import React from "react";
import translate from "../../reusable/translate";
import Tooltip from "../../materialCommitment/tooltip/Tooltip";
import ValueTypeMeter from "./ValueTypeMeter";
import RealizedTypeMeter from "./RealizedTypeMeter";

import { bindActionCreators } from "redux";
import { useDispatch, useSelector } from "react-redux";

import commitmentSlice from "../../../state/slices/commitmentSlice";
import validatorSlice from "../../../state/slices/validatorSlice";
import { getMetersOfMeterPair } from "../../../utils/meterPairUtils";

const Meter = ({
  operationMeterId,
  meter,
  removeMeter,
  disabled,
  operation
}) => {
  const { language } = useSelector(state => state);
  const dispatch = useDispatch();
  const {
    changeMeterTitle,
    changeMeterValueType,
    changeMeterStartingLevel,
    changeMeterTargetLevel,
    resetMeter
  } = bindActionCreators(commitmentSlice.actions, dispatch);

  /** Update metername (updates both if part of meterPair, else adjust just one meter) */
  const changeMeterName = name => {
    const metersToAdjust = getMetersOfMeterPair(operation, meter);

    metersToAdjust.forEach(m => {
      //Add prefix (meterDueDate) to meter name
      const prefix = `${m.meterDueDate}: `;
      const nameWithPrefix = `${prefix}${name.substr(prefix.length)}`;
      changeMeterTitle({
        operationId: operation.operationId,
        meterId: m.meterId,
        language: language,
        meterTitle: nameWithPrefix
      });
    });
  };

  const changeStartingLevel = value => {
    changeMeterStartingLevel({
      operationId: operation.operationId,
      meterId: meter.meterId,
      startingLevel: value
    });
  };

  const changeTargetLevel = value => {
    // When changing targetLevel of meter with dueDate 2030 -> update startingLevel of meter with
    // duedate 2035 if it is not yet filled or values are in "sync".
    const meterCounterPart = operation.meters.find(
      m => m.meterPairId === meter.meterPairId && m.meterDueDate === "2035"
    );
    if (
      meter.meterDueDate === "2030" &&
      meterCounterPart &&
      (meterCounterPart.startingLevel === "" ||
        meterCounterPart.startingLevel === meter.targetLevel)
    ) {
      changeMeterStartingLevel({
        operationId: operation.operationId,
        meterId: meterCounterPart.meterId,
        startingLevel: value
      });
    }
    changeMeterTargetLevel({
      operationId: operation.operationId,
      meterId: meter.meterId,
      targetLevel: value
    });
  };

  /**
   * Assign MeterValueType to meter (changes both if part of meterPair, else adjusts just one meter)
   * @param type "REALIZED" | "NUMBER"
   */
  const addMeterValueType = type => {
    const metersToAdjust = getMetersOfMeterPair(operation, meter);

    metersToAdjust.forEach(m =>
      changeMeterValueType({
        meterId: m.meterId,
        operationId: operation.operationId,
        meterValueType: type
      })
    );
  };

  /** Reset meter(s) (resets both if part of meterPair, else reset just one meter) */
  const resetAMeter = () => {
    const metersToReset = getMetersOfMeterPair(operation, meter);

    metersToReset.forEach(m =>
      resetMeter({ meterId: m.meterId, operationId: operation.operationId })
    );
  };

  return (
    <div
      className={`section ${
        meter.meterDueDate === "2035" ? "border-bottom" : "padding-bottom"
      }`}
    >
      <MeterHeader
        meter={meter}
        removeMeter={removeMeter}
        disabled={disabled}
        language={language}
      />

      {!meter.meterValueType && (
        <AddOwnMeterContainer
          addMeterValueType={addMeterValueType}
          language={language}
        />
      )}

      {meter.meterValueType && (
        <OwnMeterContainer
          resetMeter={resetAMeter}
          meter={meter}
          changeMeterName={changeMeterName}
          disabled={disabled}
        />
      )}

      {meter.meterValueType === "REALIZED" && (
        <RealizedTypeMeter language={language} />
      )}

      {meter.meterValueType === "NUMBER" && (
        <ValueTypeMeter
          meter={meter}
          changeStartingLevel={changeStartingLevel}
          changeTargetLevel={changeTargetLevel}
          disabled={disabled}
        />
      )}
    </div>
  );
};

export default Meter;

const MeterHeader = ({ meter, removeMeter, disabled, language }) => {
  const { meterDueDate, meterType_fi_FI } = meter;
  const nameToShow =
    meterType_fi_FI ||
    translate({
      textKey: "sit.commitmentCreation.operationsMeter.871646",
      languageOverride: language
    });
  return (
    <div
      className={
        "flex-row space-between items-center meter-header margin-top-bot"
      }
    >
      <h4 className="w800">{nameToShow}</h4>
      {/** Only show remove button on first meter of a pair */}
      {meterDueDate !== "2035" && (
        <button
          className="green-button w200"
          onClick={() => removeMeter(meter)}
          disabled={disabled}
        >
          {translate({
            textKey: meter.meterPairId
              ? "sit.commitmentCreation.removeMeterPair"
              : "sit.commitmentCreation.removeMeter",
            languageOverride: language
          })}{" "}
          <i className="fas fa-trash" />
        </button>
      )}
    </div>
  );
};

const AddOwnMeterContainer = ({ addMeterValueType, language }) => {
  return (
    <div className="flex-row margin-top-bot">
      {" "}
      {/* meter.meterValueType is used to check if a meter is already selected, as all meters have meterValueType */}
      <div className="w300">
        <div className="flex-row items-center">
          <h4>
            {translate({
              textKey: "sit.commitmentCreation.addMeter.871646",
              languageOverride: language
            })}
          </h4>
          <Tooltip
            textKey={"tooltip.addMeter.871646"}
            languageOverride={language}
          />
        </div>
      </div>
      <div className="flex-column">
        <div className="flex-row items-center space-between">
          <button
            className="green-button margin-top-bot"
            onClick={() => addMeterValueType("NUMBER")}
          >
            {translate({
              textKey: "sit.commitmentCreation.numericMeter.871646",
              languageOverride: language
            })}
          </button>

          <Tooltip
            textKey={"tooltip.numericMeterInfo.871646"}
            languageOverride={language}
          />
        </div>
        <div className="flex-row items-center space-between">
          <button
            className="green-button"
            onClick={() => addMeterValueType("REALIZED")}
          >
            {translate({
              textKey: "sit.commitmentCreation.realizedMeter.871646",
              languageOverride: language
            })}
          </button>
          <Tooltip
            textKey={"tooltip.realizedMeterInfo.871646"}
            languageOverride={language}
          />
        </div>
      </div>
    </div>
  );
};

const OwnMeterContainer = ({
  meter,
  resetMeter,
  changeMeterName,
  disabled
}) => {
  const { validators, language } = useSelector(state => state);
  const dispatch = useDispatch();
  const { changeOwnMeterValidator } = bindActionCreators(
    validatorSlice.actions,
    dispatch
  );

  return (
    <div className="flex-row items center">
      <div className="w300 items-center">
        <h4>
          {translate({
            textKey:
              meter.meterValueType === "REALIZED"
                ? "sit.commitmentCreation.realizedMeter"
                : "sit.commitment.reports.nameOfMeterAndUnit",
            languageOverride: language
          })}
        </h4>
        <Tooltip
          textKey={
            meter.meterValueType === "REALIZED"
              ? "tooltip.realizedMeterNameInfo.871646"
              : "tooltip.numericMeterNameInfo.871646"
          }
          languageOverride={language}
        />
      </div>
      <div className="flex-row space-between w800">
        <input
          className={`input-text ${
            validators.ownMeter ? "" : "input-text-validated"
          }`}
          type="text"
          value={meter[`meterType_${language}`] || `${meter.meterDueDate}: `}
          disabled={disabled || meter.meterDueDate === "2035"}
          onChange={e => {
            changeOwnMeterValidator(true);
            changeMeterName(e.target.value);
          }}
        />
        {/** Only show remove button on first meter of a pair */}
        {meter.meterDueDate !== "2035" && (
          <button
            className="green-button"
            disabled={disabled}
            onClick={() => resetMeter()}
          >
            {translate({
              textKey: meter.meterPairId
                ? "sit.commitmentCreation.resetMeterPair"
                : "sit.commitmentCreation.resetMeter",
              languageOverride: language
            })}
          </button>
        )}
      </div>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/components/circularEconomyCommitment/meter/Meter.jsx